import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { AttachmentButton, MessageDropZone } from '..';
import { BaseArea } from '../../base';
import { validateLatin1String } from '../../../utils';
import * as S from './compose-textarea.styles';

export interface IComposeTextareaProps {
    disabled?: boolean;
    isNote?: boolean;
    body?: string;
    /** Attachments from parent (e.g. panel attach button). Merged with paste/drop in this component. */
    attachments?: File[];
    onChange: (body: string, attachments: File[]) => void;
    /** Optional — used by callers without a panel-level Ctrl+Enter handler (e.g. group message). */
    onSave?: (body: string, attachments: File[]) => void;
    onValidationChange?: (hasError: boolean) => void;
    ToolbarComponent?: React.ReactElement;
    textareaRef?: React.RefObject<HTMLTextAreaElement>;
    placeholder?: string;
}

export const ComposeTextarea: React.FunctionComponent<IComposeTextareaProps> = (
    props: IComposeTextareaProps,
): React.ReactElement => {
    const {
        disabled,
        attachments: parentAttachments = [],
        onChange,
        onSave,
        onValidationChange,
        textareaRef,
        ToolbarComponent,
        placeholder = 'Describe what you need help with — the team will pick it up and reply here…',
    } = props;

    const [body, setBody] = useState<string>(props.body || '');
    const [latin1ValidationError, setLatin1ValidationError] = useState<string | null>(null);

    // Fully controlled attachments: always use parentAttachments only.
    const allAttachments = parentAttachments;

    // Update internal state when props.body changes (e.g., from template selection)
    useEffect(() => {
        if (props.body !== undefined && props.body !== body) {
            setBody(props.body);
        }
    }, [props.body, body]);

    const removeAttachment =
        (attachment: File): (() => void) =>
        (): void => {
            const next = _.without(parentAttachments, attachment);
            onChange(body, next);
        };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const newBody = e.target.value;
        setBody(newBody);
        onChange(newBody, allAttachments);
        if (latin1ValidationError) {
            setLatin1ValidationError(null);
            onValidationChange?.(false);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
        const validationResult = validateLatin1String(body);
        const error = validationResult.isValid ? null : validationResult.errorMessage || null;

        setLatin1ValidationError(error);
        onValidationChange?.(!!error);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
        if (!onSave || e.key !== 'Enter' || (!e.ctrlKey && !e.metaKey)) {
            return;
        }
        e.preventDefault();
        if (disabled) {
            return;
        }
        onSave(body, allAttachments);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
        const images = _.map(e.clipboardData.items, item => {
            if (item.type.startsWith('image/')) {
                return item.getAsFile();
            }
        });
        if (images && images.length) {
            const newAttachments = _.compact(_.concat(parentAttachments, images));
            onChange(body, newAttachments);
        }
    };

    const attached = _.map(
        allAttachments,
        (attachment: File, i: number): React.ReactElement => (
            <AttachmentButton
                key={`attachment-${i}`}
                filename={attachment.name}
                filesize={attachment.size}
                onRemove={removeAttachment(attachment)}
            />
        ),
    );

    return (
        <S.Container>
            <S.Label data-compose-label>Message</S.Label>
            <MessageDropZone
                disabled={disabled}
                onFilesDrop={files => {
                    const newAttachments = _.concat(parentAttachments, files);
                    onChange(body, newAttachments);
                }}>
                {({ isDragging, containerProps }) => (
                    <S.MessageBox {...containerProps}>
                        <S.InnerCol>
                            {isDragging && <S.DragOverlay>Drop files here to attach</S.DragOverlay>}
                            <BaseArea
                                ref={textareaRef as any}
                                rows={5}
                                disabled={disabled}
                                value={body}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                placeholder={placeholder}
                                disableLatin1Validation={true}
                                errorMessage={latin1ValidationError || undefined}
                            />
                            {attached && attached.length ? <S.InputAttachments>{attached}</S.InputAttachments> : null}
                            {ToolbarComponent}
                        </S.InnerCol>
                    </S.MessageBox>
                )}
            </MessageDropZone>
        </S.Container>
    );
};
